import apiClient from './api';

export type NewsArticleStatus = 'draft' | 'in_review' | 'published' | 'rejected' | 'failed';
export type NewsArticleStepId = 'ingest' | 'translate' | 'fact-check' | 'review' | 'publish';
export type NewsArticleStepStatus = 'pending' | 'processing' | 'completed' | 'failed' | 'skipped';
export type NewsArticleFactCheckVerdict = 'corroborated' | 'contradicted' | 'unverified';

export interface NewsArticleStep {
  id: NewsArticleStepId;
  name: string;
  status: NewsArticleStepStatus;
  order: number;
  startedAt?: string;
  completedAt?: string;
  canEdit?: boolean;
  error?: string;
  providerUsed?: string;
}

export interface NewsArticleFactCheckClaim {
  claim: string;
  verdict: NewsArticleFactCheckVerdict;
  supportingArticleIds: string[];
  note: string;
}

export interface NewsArticleFactCheck {
  status: 'not_run' | 'completed' | 'failed';
  corroboratingArticleIds: string[];
  contradictingArticleIds: string[];
  claims: NewsArticleFactCheckClaim[];
  editorNotes?: string;
  runAt?: string;
}

export interface NewsArticle {
  _id: string;
  organizationId: string;
  sourceId: string;
  outlet: string;
  status: NewsArticleStatus;
  steps: NewsArticleStep[];
  originalTitle: string;
  originalBody: string;
  originalLanguage: 'en' | 'ta';
  bodyIsSummary: boolean;
  sourceUrl: string;
  publishedAt: string;
  translatedTitle?: string;
  translatedBody?: string;
  translationProviderUsed?: string;
  translationEditedByUser?: boolean;
  factCheck?: NewsArticleFactCheck;
  publishedTtsJobId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface NewsSource {
  _id: string;
  organizationId: string;
  providerType: 'rss' | 'wire-api';
  name: string;
  outlet: string;
  language: 'en' | 'ta';
  config: { feedUrl?: string };
  isActive: boolean;
  pollIntervalMinutes: number;
  lastPolledAt?: string;
  lastPollStatus?: 'success' | 'error' | 'skipped';
  lastPollError?: string;
  createdAt: string;
  updatedAt: string;
}

export interface GlossaryRule {
  _id: string;
  name: string;
  language?: string;
  isActive: boolean;
  data: { rules?: Array<{ find: string; replace: string; matchType: string; caseSensitive: boolean }> };
}

export const newsroomApi = {
  getFeatureStatus: async (): Promise<{ enabled: boolean }> => {
    const response = await apiClient.get<{ success: boolean; data: { enabled: boolean } }>('/newsroom/feature-status');
    return response.data.data;
  },

  // Sources
  getSources: async (): Promise<NewsSource[]> => {
    const response = await apiClient.get<{ success: boolean; data: NewsSource[] }>('/newsroom/sources');
    return response.data.data;
  },
  createSource: async (data: {
    providerType: 'rss' | 'wire-api';
    name: string;
    outlet: string;
    language: 'en' | 'ta';
    feedUrl?: string;
    pollIntervalMinutes?: number;
  }): Promise<NewsSource> => {
    const response = await apiClient.post<{ success: boolean; data: NewsSource }>('/newsroom/sources', data);
    return response.data.data;
  },
  updateSource: async (id: string, data: Partial<NewsSource>): Promise<NewsSource> => {
    const response = await apiClient.put<{ success: boolean; data: NewsSource }>(`/newsroom/sources/${id}`, data);
    return response.data.data;
  },
  deleteSource: async (id: string): Promise<void> => {
    await apiClient.delete(`/newsroom/sources/${id}`);
  },
  pollSourceNow: async (id: string): Promise<{ created: number; skipped: number }> => {
    const response = await apiClient.post<{ success: boolean; data: { created: number; skipped: number } }>(
      `/newsroom/sources/${id}/poll`
    );
    return response.data.data;
  },

  // Articles
  getArticles: async (params?: {
    status?: NewsArticleStatus;
    outlet?: string;
    language?: 'en' | 'ta';
    search?: string;
    dateFrom?: string;
    dateTo?: string;
    sortBy?: 'publishedAt' | 'createdAt';
    sortOrder?: 'asc' | 'desc';
    page?: number;
    limit?: number;
  }): Promise<{ articles: NewsArticle[]; pagination: { page: number; limit: number; total: number } }> => {
    const response = await apiClient.get<{
      success: boolean;
      data: NewsArticle[];
      pagination: { page: number; limit: number; total: number };
    }>('/newsroom/articles', { params });
    return { articles: response.data.data, pagination: response.data.pagination };
  },
  getArticle: async (id: string): Promise<NewsArticle> => {
    const response = await apiClient.get<{ success: boolean; data: NewsArticle }>(`/newsroom/articles/${id}`);
    return response.data.data;
  },
  updateArticle: async (
    id: string,
    data: { translatedTitle?: string; translatedBody?: string; editorNotes?: string }
  ): Promise<NewsArticle> => {
    const response = await apiClient.put<{ success: boolean; data: NewsArticle }>(`/newsroom/articles/${id}`, data);
    return response.data.data;
  },
  translateArticle: async (id: string): Promise<NewsArticle> => {
    const response = await apiClient.post<{ success: boolean; data: NewsArticle }>(`/newsroom/articles/${id}/translate`);
    return response.data.data;
  },
  factCheckArticle: async (id: string): Promise<NewsArticle> => {
    const response = await apiClient.post<{ success: boolean; data: NewsArticle }>(`/newsroom/articles/${id}/fact-check`);
    return response.data.data;
  },
  // Retries whichever step is 'failed' (translate/fact-check). Publish
  // failures are rejected server-side — re-publish via the Publish dialog.
  retryArticle: async (id: string): Promise<NewsArticle> => {
    const response = await apiClient.post<{ success: boolean; data: NewsArticle }>(`/newsroom/articles/${id}/retry`);
    return response.data.data;
  },
  publishArticle: async (
    id: string,
    data: { voiceId: string; provider?: 'elevenlabs' | 'sarvam' | 'ai4bharat' }
  ): Promise<{ ttsJobId: string }> => {
    const response = await apiClient.post<{ success: boolean; data: { ttsJobId: string } }>(
      `/newsroom/articles/${id}/publish`,
      data
    );
    return response.data.data;
  },

  // Glossary (thin passthrough — all mutations go through /configurations)
  getGlossary: async (): Promise<GlossaryRule[]> => {
    const response = await apiClient.get<{ success: boolean; data: GlossaryRule[] }>('/newsroom/glossary');
    return response.data.data;
  },
};
